/**
 * Sector rotation map (issue #365): where each sector stands against the
 * all-sector average, and whether that lead is growing or fading.
 *
 * DESCRIPTIVE ONLY. Issue #363 measured 27 years of sector data and found that
 * neither axis here ranks the next 1-10 sessions' relative returns
 * (scripts/research/SECTOR_ROTATION_STUDY.md) -- never present this as a forecast.
 *
 * Pure and dependency-free so a worker can import it cross-tree. It reads only
 * `sector_performance` rows (one settled move per sector per session), so the
 * state for ANY past session is re-derivable on demand -- which is why no
 * rotation table exists: a stored copy would sit beside the inputs it derives from.
 */

export const RS_WINDOW = 20;       // sessions of relative strength
export const MOMENTUM_WINDOW = 5;  // sessions over which the change in RS is measured
export const TRAIL_LENGTH = 5;     // recent positions kept per sector, newest last

export const QUADRANTS = {
  leading: { label: 'Leading', blurb: 'ahead of the average, lead growing' },
  weakening: { label: 'Weakening', blurb: 'ahead of the average, lead shrinking' },
  lagging: { label: 'Lagging', blurb: 'behind the average, gap widening' },
  improving: { label: 'Improving', blurb: 'behind the average, gap closing' },
};

/** The one ladder. `>= 0` on both axes so a sector exactly on a line has a home. */
export const classifyQuadrant = (rs, momentum) => {
  if (rs >= 0) return momentum >= 0 ? 'leading' : 'weakening';
  return momentum >= 0 ? 'improving' : 'lagging';
};

/**
 * A sector's move for one session: cap-weighted when present, else the plain
 * mean. The ONE selector -- the "Over time" chart compounds the same field.
 * Absent on both -> null, never 0 (a missing session is not a flat one).
 */
export const sectorMovePct = (row) => {
  const move = row?.cap_weighted_change_pct ?? row?.mean_change_pct;
  return typeof move === 'number' && Number.isFinite(move) ? move : null;
};

const mean = (xs) => xs.reduce((a, x) => a + x, 0) / xs.length;

/**
 * @param {Array} rows `sector_performance` rows: { sector, date, cap_weighted_change_pct, mean_change_pct }
 * @returns {{ asOf, sessionsAvailable, sessionsNeeded, points }} `points` is empty (and `asOf`
 *   null) when fewer sessions are stored than the windows need. Each point is
 *   { sector, rs, momentum, quadrant, trail: [{ date, rs, momentum }] } with `rs` and
 *   `momentum` in percentage POINTS. A sector missing any session its windows touch is omitted.
 */
export function buildSectorRotation(rows, { rsWindow = RS_WINDOW, momentumWindow = MOMENTUM_WINDOW, trail = TRAIL_LENGTH } = {}) {
  const dates = [...new Set((rows || []).map((r) => r.date))].sort();
  // rsWindow moves to compound, momentumWindow more to look back, trail-1 more for the path
  const sessionsNeeded = rsWindow + momentumWindow + trail - 1;
  const empty = { asOf: null, sessionsAvailable: dates.length, sessionsNeeded, points: [] };
  if (dates.length < sessionsNeeded) return empty;

  const used = dates.slice(-sessionsNeeded);
  const indexOf = new Map(used.map((d, i) => [d, i]));
  const movesBySector = new Map();
  for (const row of rows) {
    const i = indexOf.get(row.date);
    const move = sectorMovePct(row);
    if (i === undefined || move === null) continue;
    if (!movesBySector.has(row.sector)) movesBySector.set(row.sector, new Array(used.length).fill(null));
    movesBySector.get(row.sector)[i] = move;
  }
  const complete = [...movesBySector].filter(([, moves]) => moves.every((m) => m !== null));
  if (!complete.length) return empty;

  // compounded % over the rsWindow sessions ending at index `end`
  const windowReturn = (moves, end) => {
    let growth = 1;
    for (let i = end - rsWindow + 1; i <= end; i += 1) growth *= 1 + moves[i] / 100;
    return (growth - 1) * 100;
  };
  // relative strength of every complete sector at `end`, against their equal-weighted average
  const rsAt = (end) => {
    const returns = complete.map(([, moves]) => windowReturn(moves, end));
    const average = mean(returns);
    return returns.map((r) => r - average);
  };

  const last = used.length - 1;
  const rsByEnd = new Map();
  for (let end = rsWindow - 1; end <= last; end += 1) rsByEnd.set(end, rsAt(end));

  const points = complete.map(([sector], s) => {
    const path = [];
    for (let end = last - trail + 1; end <= last; end += 1) {
      const rs = rsByEnd.get(end)[s];
      path.push({ date: used[end], rs, momentum: rs - rsByEnd.get(end - momentumWindow)[s] });
    }
    const now = path[path.length - 1];
    return { sector, rs: now.rs, momentum: now.momentum, quadrant: classifyQuadrant(now.rs, now.momentum), trail: path };
  });

  return { asOf: used[last], sessionsAvailable: dates.length, sessionsNeeded, points };
}
